import { useCallback, useMemo } from 'react';

import { fetchData } from 'apis/DataAPI';
import { WIDGET_TYPES } from 'common/Widgets/constants';
import { DATA_RANGE_TYPE } from 'common/Widgets/Widget/constants';
import { isCommunityCCRC } from 'components/Community/utils';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { getQuarterEndDate } from 'utils/dateQuarter';

import useCommunityContractSelection from './useCommunityContractSelection';
import useDataQuarterSelection from './useDataQuarterSelection';

const RATING_WIDGET_TYPES = [
  WIDGET_TYPES.RATING,
  WIDGET_TYPES.RATING_GRAPH,
  WIDGET_TYPES.REVIEWS_SUMMARY,
  WIDGET_TYPES.MARKET_POSITIONING,
];

const shouldShowContractLabelAnnotations = (
  community: any,
  isCustomContractFilterVisible: boolean
) => {
  if (isCustomContractFilterVisible) {
    return true;
  }
  const rentalTypes = Array.isArray(community?.rental_types) ? community.rental_types : [];

  return isCommunityCCRC(rentalTypes);
};

type FetchDataArgs = Parameters<typeof fetchData>;

export const useWidgetDataFetcher = () => {
  const { community, isViewSalesBattlecard } = useCommunityViewContext();
  const { snapshotUuids, overtimeSnapshotUuids, selection, isLatestQuarterSelected } = useDataQuarterSelection();
  const { communityContractIds, isFilterApplicable } = useCommunityContractSelection();

  const isCustomContractFilterVisible = isFilterApplicable && !isViewSalesBattlecard;
  const showContractLabelAnnotations = useMemo(
    () => shouldShowContractLabelAnnotations(community, isCustomContractFilterVisible),
    [community, isCustomContractFilterVisible]
  );
  const ratingCutoffDate = useMemo(() => {
    if (isLatestQuarterSelected) {
      return null;
    }
    return getQuarterEndDate(selection?.selectedQuarterSlug ?? null);
  }, [selection?.selectedQuarterSlug, isLatestQuarterSelected]);

  const fetchWidgetData = useCallback(
    (
      communityId: FetchDataArgs[0],
      widgetType: FetchDataArgs[1],
      rangeType: FetchDataArgs[2],
      payload: FetchDataArgs[3],
      config?: FetchDataArgs[4]
    ) => {
      const shouldApplyRatingCutoff = Boolean(ratingCutoffDate) && RATING_WIDGET_TYPES.includes(widgetType);
      const payloadWithRatingFilters = shouldApplyRatingCutoff
        ? {
            ...payload,
            filters: {
              ...payload.filters,
              end_created_at: ratingCutoffDate,
            },
          }
        : payload;

      const payloadWithContractOverrides = communityContractIds
        ? {
            ...payloadWithRatingFilters,
            filters: {
              ...payloadWithRatingFilters.filters,
              community_contracts: communityContractIds,
            },
          }
        : payloadWithRatingFilters;

      return fetchData(communityId, widgetType, rangeType, {
        ...payloadWithContractOverrides,
        show_contract_label_annotations: showContractLabelAnnotations,
        snapshots: rangeType === DATA_RANGE_TYPE.OVERTIME ? (overtimeSnapshotUuids ?? null) : (snapshotUuids ?? null),
      });
    },
    [snapshotUuids, overtimeSnapshotUuids, showContractLabelAnnotations, ratingCutoffDate, communityContractIds]
  );

  return { fetchWidgetData, showContractLabelAnnotations, snapshotUuids, communityContractIds };
};

export default useWidgetDataFetcher;
